import React, { useEffect, useState } from "react";
import { useProduct } from "@montana/containers/product";
import { Media } from "@montana/shared/media";
import { IImage } from "@montana/models/generated/cms.interface";
import { isBrowser } from "@montana/helpers/browser.helper";
import { getWindowSize } from "@montana/helpers/on-resize.helper";
import { DynamicErrorHandler } from "@montana/shared/dynamic-error-handler";
import { staticSettings } from "@montana/helpers/static-settings.helper";
import { Spinner } from "@montana/shared/spinner/spinner.component";

export const AssetProductViewer = () => {
    const { prices, productTitleH1, setProductImageUpdating } = useProduct();
    const [image, setImage] = useState<IImage | undefined>();
    const [show, setShow] = useState(true);

    useEffect(() => {
        if (prices?.productImageViewModel?.useAssetImage) {
            setImage({
                ...prices.productImageViewModel.assetImageViewModel,
                altText: productTitleH1
            });
        } else {
            setImage({
                url:
                    prices?.productImageViewModel?.londonDynamicsStaticImageUrl,
                altText: productTitleH1
            });
        }
    }, [prices]);

    useEffect(() => {
        setShow(false);
        setProductImageUpdating(false);
    }, [image]);

    useEffect(() => {
        if (!show) {
            setShow(true);
        }
    }, [show]);

    return (
        <div className="asset-product-viewer">
            {show && <Media image={image} mediaRatio={1} />}
        </div>
    );
};

const LazyZoomPan = React.lazy(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (): Promise<{ default: React.ComponentType<any> }> =>
        import(/* webpackChunkName: "zoom-pan" */ "./zoom-pan.component")
);

export const ZoomedAssetProductViewer = () => {
    const { prices, productTitleH1, setProductImageUpdating } = useProduct();
    const [image, setImage] = useState<string>();
    const [show, setShow] = useState(true);

    useEffect(() => {
        let imageUrl: string | undefined;
        if (prices?.productImageViewModel?.useAssetImage) {
            imageUrl = prices.productImageViewModel.assetImageViewModel?.url;
        }

        imageUrl = imageUrl?.startsWith("/")
            ? `${staticSettings.apiUrl}${imageUrl}`
            : imageUrl;
        if (imageUrl) {
            if (isBrowser) {
                const { height } = getWindowSize();
                const imageHeight =
                    height * 4 > 4000
                        ? 4000
                        : Math.round((height * 4) / 100) * 100;
                imageUrl = `${imageUrl}?mode=crop&height=${imageHeight}`;
            }
            setImage(imageUrl);
        }
    }, [prices]);

    useEffect(() => {
        setShow(false);
        setProductImageUpdating(false);
    }, [image]);

    useEffect(() => {
        if (!show && image) {
            setShow(true);
        }
    }, [show, image]);

    return (
        <div className="zoomed-asset-product-viewer">
            {show && image && (
                <DynamicErrorHandler kind={`LazyPanHandler`}>
                    <React.Suspense key={image} fallback={<Spinner />}>
                        <LazyZoomPan
                            imageUrl={image}
                            altText={productTitleH1 || ""}
                        />
                    </React.Suspense>
                </DynamicErrorHandler>
            )}
        </div>
    );
};
